import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DefaultNodeOptions,
  StringNode,
  widget,
  WidgetPlugin,
} from '@sagold/react-json-editor';
import clsx from 'clsx';
import dayjs from 'dayjs';

import { SingleDatePicker } from '../../../components/datepicker/SingleDatePicker';
import { FormField } from '../../../components/formfield/FormField';
import { FormLabel } from '../../../components/formlabel/FormLabel';
import { ReadOnlyInput } from '../../../components/readonly-input/ReadOnlyInput';
import {
  getDateFromJsonRef,
  getDateFromString,
} from '../../widgets/widgetUtils';
import {
  isNullableString,
  resolvePlaceholder,
  useErrorTransformation,
} from '../widgetUtils';

import inputStyles from '../../../components/input/input.module.scss';

export type DateOptions = DefaultNodeOptions<{
  showTitle?: boolean;
  clearable?: boolean;
  loading?: boolean;
  tableLayout?: boolean;
  minDate?: string;
  minDateRef?: string;
  maxDate?: string;
  maxDateRef?: string;
  showErrorWithColor?: boolean;
}>;

const VALUE_DATE_FORMAT = 'YYYY-MM-DD';

export const DateWidget = widget<StringNode<DateOptions>, string | null>(
  ({ node, options, setValue, editor }) => {
    const { t } = useTranslation();
    //Make sure the initial value is formatted correctly
    useEffect(() => {
      // Don't apply formatting to readonly values
      if (options.readOnly) {
        return;
      }
      if (node.value == null || !dayjs(node.value).isValid()) {
        setValue(null);
        return;
      }
      // Valid date above; here we only check strict YYYY-MM-DD formatting
      // (e.g. ISO datetimes are valid dates but not in the expected format).
      if (!dayjs(node.value, VALUE_DATE_FORMAT, true).isValid()) {
        setValue(dayjs(node.value).format(VALUE_DATE_FORMAT));
      }
    }, [node.value, options.readOnly, setValue]);

    const date = node.value ? new Date(node.value) : null;
    const isValidDate = date ? !Number.isNaN(date.getTime()) : true;

    const errors = useErrorTransformation(node.errors);

    const minDate = options?.minDate
      ? getDateFromString(options.minDate)
      : options?.minDateRef
        ? getDateFromJsonRef(options.minDateRef, editor.root, node)
        : undefined;
    const maxDate = options?.maxDate
      ? getDateFromString(options.maxDate)
      : options?.maxDateRef
        ? getDateFromJsonRef(options.maxDateRef, editor.root, node)
        : undefined;

    return (
      <FormField
        widgetType="select"
        node={node}
        errors={errors}
        options={options}
        tableLayout={options.tableLayout}
      >
        <div
          className={clsx({
            [inputStyles['root--table-layout']]: options.tableLayout,
          })}
        >
          {options.readOnly ? (
            <ReadOnlyInput
              description={options.description}
              value={node.value}
              placeholder={options.placeholder}
              label={options.title}
              showTitle={options.showTitle !== false}
              tableLayout={options.tableLayout}
              hasError={options.showErrorWithColor && node.errors.length > 0}
            />
          ) : (
            <>
              <FormLabel
                description={options.description}
                required={options.required}
                disabled={options.disabled}
                error={node.errors.length > 0}
                label={options.showTitle !== false && options.title}
              />
              <SingleDatePicker
                placeholder={resolvePlaceholder(
                  options.placeholder,
                  t('app.forms.placeholders.date')
                )}
                clearable={options.clearable}
                disabled={options.disabled}
                readOnly={options.readOnly}
                loading={options.loading}
                value={isValidDate ? date : null}
                minDate={minDate}
                maxDate={maxDate}
                onChange={(value) =>
                  setValue(
                    value != null
                      ? dayjs(value).format(VALUE_DATE_FORMAT)
                      : null
                  )
                }
              />
            </>
          )}
        </div>
      </FormField>
    );
  }
);

export const DateWidgetPlugin: WidgetPlugin = {
  id: 'date-widget',
  use: (node) => {
    if (node.schema.format === 'date') {
      if (isNullableString(node.schema.type)) {
        return true;
      }

      if (Array.isArray(node.schema.anyOf) && node.schema.anyOf.length > 0) {
        return node.schema.anyOf.some(
          // for this specific use-case, we don't want to check nested type to be isNullableString
          (el) => typeof el === 'object' && el.type === 'string'
        );
      }
    }

    return false;
  },
  Widget: DateWidget,
};
